<template>
  <tabs />
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <export
     :url="`/focus_points/${props.modelValue?.id}/campaign_registrations/csv`"
      :filter="totalFilter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="csvTitle"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    />
  </toolbar>

  <div class="ml-12">
    <div class="table">
      <data-table
        class="mb-auto"
        v-model:sort-by="sortKey"
        v-model:sort-direction="sortDirection"
        :busy="loading"
        :items="records"
        :row-link="
          (item) => ({
            name: 'driver-focus-points',
            params: { driverId: item?.campaignRegistration?.driver?.id ?? '-' },
          })
        "
        :columns="columns"
        :visible-columns="visibleColumns"
      />
    </div>
  </div>
  <pagination v-model:page="page" v-model:per-page="perPage" class="pagination" :total="total" />
</template>

<script lang="tsx" setup>
import { ref, reactive, computed, unref } from 'vue';
import { useI18n } from 'vue-i18n';
import FilterPane from '@utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@utils/toolbar/panes/AddFields.vue';
import Export from '@components/utils/toolbar/panes/Export.vue';
import Toolbar from '@utils/toolbar/Toolbar.vue';
import Pagination from '@utils/table/Pagination.vue';
import DataTable, { type Column } from '@utils/table/Table.vue';
import {collOptionsProvider, enumOptionsProvider, ProviderParams, useProvider} from '@utils/table/hooks/provider';
import { format } from 'date-fns';
import type { Filter } from '@utils/filter/Filter.vue';
import Tabs from '@components/structure/navigation/Tabs.vue';
import type {FocusPoint, HydraCollection, Payout} from '@/entities/Entities';
import { useQueryRef } from '@/hooks/url';
import {Campaign, Reward} from '@/entities/Entities';
import {formatTimeDurationFromSeconds} from '@/utils/helpers';
import {useTableFields} from '@hooks/table-configuration';

interface Props {
  modelValue: FocusPoint | null;
}

const props = defineProps<Props>();

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 10);
const sortKey = useQueryRef<string | undefined>('sortKey', undefined);
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', undefined);

const { t } = useI18n();

const csvTitle = computed(() => {
  return t('csv.title.driversForFocusPoint', {
    focusPoint: props.modelValue?.name,
  });
});

const filter = useQueryRef('filter', {});
const fixedFilter = computed(() => ({
}));
const totalFilter = computed(() => {
  return { ...filter.value, ...fixedFilter.value };
});

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter: totalFilter,
  pagination: {
    page,
    perPage,
  },
});

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const columns = computed<ColumnDefinition[]>(() => ([
  {
    key: 'id',
    title: t('id'),
    group: t('pages.driver.focusPointDetails.fieldGroups.general'),
    computed: (item) => item.campaignRegistration?.id,
    class: 'whitespace-nowrap',
    visible: false,
    sortable: false,
  },
  {
    key: 'driver.user.firstName',
    title: t('user.firstName'),
    group: t('pages.driver.focusPointDetails.fieldGroups.general'),
    computed: (item) => item.campaignRegistration?.driver?.user?.firstName,
    sortable: true,
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.driver.focusPointDetails.filterGroups.general'),
      options: {
        label: t('user.firstName'),
      },
    },
  },
  {
    key: 'driver.user.lastName',
    title: t('user.lastName'),
    group: t('pages.driver.focusPointDetails.fieldGroups.general'),
    computed: (item) => item.campaignRegistration?.driver?.user?.lastName,
    sortable: true,
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.driver.focusPointDetails.filterGroups.general'),
      options: {
        label: t('user.lastName'),
      },
    },
  },
  {
    key: 'driver.user.email',
    title: t('user.email'),
    group: t('pages.driver.focusPointDetails.fieldGroups.general'),
    computed: (item) => item.campaignRegistration?.driver?.user?.email,
    sortable: true,
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.driver.focusPointDetails.filterGroups.general'),
      options: {
        label: t('user.email'),
      },
    },
  },
  {
    key: 'numberOfPhotos',
    sortKey: `${props.modelValue?.id}][numberOfPhotos`,
    title: t('focusPoint.totalPhotos'),
    group: t('pages.driver.focusPointDetails.fieldGroups.general'),
    visible: true,
    sortable: true,
  },
  {
    key: 'standingTime',
    sortKey: `${props.modelValue?.id}][standingTime`,
    title: t('focusPoint.totalTime'),
    group: t('pages.driver.focusPointDetails.fieldGroups.general'),
    formatter: (v) => (v ? formatTimeDurationFromSeconds(v) : '00:00:00'),
    visible: true,
    sortable: true,
  },
  {
    key: 'numberOfStops',
    sortKey: `${props.modelValue?.id}][numberOfStops`,
    title: t('focusPoint.totalStops'),
    group: t('pages.driver.focusPointList.fieldGroups.general'),
    visible: true,
    sortable: true,
  },
]));

const visibleColumns = useTableFields(
    columns,
);

const blockRequest = computed(() => !props.modelValue?.id);


const {
  data: items,
  loading,
  refetch,
} = useProvider<HydraCollection<Payout>>(computed(() => `/focus_points/${props.modelValue?.id}/campaign_registrations`), providerParams, {
  disabled: blockRequest,
});

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const filters = computed(() =>
  unref(columns).flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter))
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    return [{ key: col.key, ...col.filter }];
  })
);
</script>

<style lang="scss" scoped>
button {
  @apply block;
}

.table {
  @apply flex flex-col;
}
</style>
